import { useState } from "react";
import { Plus, Tag } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDeleteFileByIdBo } from "@/api/file.api";
import { Button } from "@/components/ui/button";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { ExpeditionDocumentCard } from "../../expeditionDocumentCard";
import { UploadFileBoModal } from "../../uploadFileModal";

export const ShippingLabel = ({ expedition }: { expedition: GetOneExpeditionBoResponseDto }) => {
  const { t } = useTranslation("boExpeditionForm");
  const queryClient = useQueryClient();
  const { mutateAsync: deleteFile } = useDeleteFileByIdBo();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const shippingLabels = (expedition.expeditionDocuments?.documents ?? []).filter(
    (d) => d.type === FileTypeEnum.EXPEDITION_SHIPPING_LABEL,
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["expedition", "bo", expedition.id] });
  };

  const handleDelete = async (fileId: string) => {
    await deleteFile(fileId);
    invalidate();
    toast.success(t("shippingLabel.deleteSuccess"));
  };

  return (
    <AccordionItem
      value="shippingLabel"
      className="shadow-basic flex w-full max-w-[1400px] flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-slate-100 p-6"
    >
      <AccordionTrigger className="flex w-full flex-row items-start justify-between gap-2 self-start !no-underline">
        <div className="flex flex-row items-center gap-2 self-start">
          <Tag className="h-6 w-6 !rotate-0 text-blue-600" />
          <p className="text-xl font-bold">{t("shippingLabel.title")}</p>
        </div>
      </AccordionTrigger>
      <AccordionContent forceMount className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3">
          {shippingLabels.length === 0 ? (
            <p className="py-2 text-center text-sm text-gray-500">{t("shippingLabel.empty")}</p>
          ) : (
            shippingLabels.map((document) => (
              <ExpeditionDocumentCard key={document.id} file={document} onDelete={handleDelete} />
            ))
          )}
          <Button
            type="button"
            variant="ghost"
            className="h-auto self-start p-0 text-blue-600 no-underline hover:bg-transparent hover:text-blue-700"
            onClick={() => setUploadModalOpen(true)}
          >
            <Plus className="mr-1 size-4" />
            {t("shippingLabel.upload")}
          </Button>
        </div>
        <UploadFileBoModal
          expedition={expedition}
          open={uploadModalOpen}
          accept={["application/pdf"]}
          onOpenChange={setUploadModalOpen}
          expeditionDocumentsId={expedition.expeditionDocuments?.id}
          expeditionId={expedition.id}
          allowedFileTypes={[FileTypeEnum.EXPEDITION_SHIPPING_LABEL]}
          invalidateQueryKey={["expedition", "bo", expedition.id]}
          defaultOptions={{
            isPrivate: true,
            sendViaApi: false,
          }}
          disabledOptions={{
            isPrivate: true,
            sendViaApi: true,
          }}
        />
      </AccordionContent>
    </AccordionItem>
  );
};
